import BrandMetadata from './BrandMetadata';
import { PRODUCT_IDENTITIES, productForLocation } from './productIdentity';
import ProductOptions from './ProductOptions';
import './ProductIdentity.css';
import WorkstationSettings from './WorkstationSettings';
import { BecaisseAudienceVisit } from './BecaisseAudienceVisit';
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import ClientView from './ClientView';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';
import MasterConsole from './MasterConsole';
import AttractScreen from './AttractScreen';
import LoyaltyEntryScreen from './LoyaltyEntryScreen';
import LoyaltyValidationScreen from './LoyaltyValidationScreen';
import PanelView from './PanelView';
import ClickCollectView, { ClickCollectStatus } from './ClickCollectView';
import ClickCollectTrackingView from './ClickCollectTrackingView';
import CuisineView from './CuisineView';
import EtatView from './EtatView';
import CaisseView from './CaisseView';
import ReservationPage from './ReservationPage';
import CaisseReservationsView from './CaisseReservationsView';
import CustomerChatbotPage from './CustomerChatbotPage';
import { API_URL, buildKioskAuthHeaders, resolveMediaUrl } from './api';
import { rememberVerifiedKioskSession, forgetVerifiedKioskSession, hasVerifiedOfflineKioskSession } from './offlineKioskAccess';
import './App.css';

const KIOSK_BACKGROUND_ROUTES = new Set(['/', '/veille', '/welcome', '/client', '/loyalty/validate']);
const PROTECTED_KIOSK_ROUTES = new Set(['/', '/veille', '/welcome', '/client', '/loyalty/validate', '/panel', '/etat', '/cuisine', '/caisse', '/caisse/reservations', '/pad']);
const DAY_INDEX_TO_KEY = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const KIOSK_SETTINGS_CACHE_KEY = 'maythai_kiosk_settings_cache_v1';
function kioskSettingsCacheKey() {
  try { return `${KIOSK_SETTINGS_CACHE_KEY}:${JSON.parse(localStorage.getItem('kioskAccount') || '{}').id || 'anonymous'}`; }
  catch (_) { return `${KIOSK_SETTINGS_CACHE_KEY}:anonymous`; }
}

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: String(error?.message || 'Erreur inattendue'),
    };
  }

  componentDidCatch(error) {
    console.error('Route render error:', error);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="panel-empty" style={{ margin: '24px' }}>
        <strong>Une erreur est survenue dans l’interface.</strong>
        <div style={{ marginTop: '8px' }}>{this.state.errorMessage}</div>
      </div>
    );
  }
}

function ExternalBefidRedirect() {
  useEffect(() => {
    const configuredUrl = String(process.env.REACT_APP_BEFID_URL || '').trim();
    const isPublicServer = window.location.protocol === 'https:' || window.location.hostname === 'beborne.com';
    const targetUrl = configuredUrl || (
      isPublicServer
        ? 'https://befid.fr/befid-app/login'
        : `${window.location.protocol}//${window.location.hostname}:8095/login`
    );
    window.location.replace(targetUrl);
  }, []);

  return <div className="panel-empty">Ouverture de BEFID...</div>;
}

function parseTimeToMinutes(value) {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

function normalizeWeeklySchedule(rawSchedule, fallback = {}) {
  let parsed = rawSchedule;
  if (typeof rawSchedule === 'string') {
    const trimmed = rawSchedule.trim();
    if (!trimmed) {
      parsed = null;
    } else {
      try {
        parsed = JSON.parse(trimmed);
      } catch (error) {
        parsed = null;
      }
    }
  }

  const fallbackDay = {
    enabled: true,
    openingTime: String(fallback.openingTime || '').trim(),
    closingTime: String(fallback.closingTime || '').trim(),
    breakStartTime: String(fallback.breakStartTime || '').trim(),
    breakEndTime: String(fallback.breakEndTime || '').trim(),
  };

  const schedule = {};
  DAY_INDEX_TO_KEY.forEach((dayKey) => {
    const dayRaw = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed[dayKey] : null;
    schedule[dayKey] = {
      enabled: dayRaw?.enabled !== undefined ? !!dayRaw.enabled : true,
      openingTime: String(dayRaw?.openingTime ?? fallbackDay.openingTime).trim(),
      closingTime: String(dayRaw?.closingTime ?? fallbackDay.closingTime).trim(),
      breakStartTime: String(dayRaw?.breakStartTime ?? fallbackDay.breakStartTime).trim(),
      breakEndTime: String(dayRaw?.breakEndTime ?? fallbackDay.breakEndTime).trim(),
    };
  });
  return schedule;
}

function getOpeningStatus(settings) {
  const schedule = normalizeWeeklySchedule(settings?.weeklySchedule, settings || {});
  const now = new Date();
  const dayKey = DAY_INDEX_TO_KEY[now.getDay()];
  const daySchedule = schedule[dayKey];

  if (!daySchedule || daySchedule.enabled === false) {
    return { isOpen: false };
  }

  const openingMinutes = parseTimeToMinutes(daySchedule.openingTime);
  const closingMinutes = parseTimeToMinutes(daySchedule.closingTime);
  const breakStartMinutes = parseTimeToMinutes(daySchedule.breakStartTime);
  const breakEndMinutes = parseTimeToMinutes(daySchedule.breakEndTime);

  if (openingMinutes === null || closingMinutes === null || closingMinutes <= openingMinutes) {
    return { isOpen: true };
  }

  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const hasBreak =
    breakStartMinutes !== null &&
    breakEndMinutes !== null &&
    breakStartMinutes > openingMinutes &&
    breakEndMinutes > breakStartMinutes &&
    breakEndMinutes < closingMinutes;

  if (currentMinutes < openingMinutes) {
    return { isOpen: false };
  }
  if (hasBreak && currentMinutes >= breakStartMinutes && currentMinutes < breakEndMinutes) {
    return { isOpen: false };
  }
  if (currentMinutes >= closingMinutes) {
    return { isOpen: false };
  }
  return { isOpen: true };
}

function KioskBackgroundLayer() {
  const location = useLocation();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!KIOSK_BACKGROUND_ROUTES.has(location.pathname)) return;
    let active = true;
    let intervalId = null;
    const shouldPoll = location.pathname === '/' || location.pathname === '/veille';

    const persistSettings = (payload) => {
      try {
        localStorage.setItem(kioskSettingsCacheKey(), JSON.stringify(payload));
      } catch (error) {
        // Ignore local cache write failures on kiosk devices.
      }
    };

    const restoreCachedSettings = () => {
      try {
        const raw = localStorage.getItem(kioskSettingsCacheKey());
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    };

    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/printer-settings`, {
          headers: buildKioskAuthHeaders(),
        });
        const payload = await response.json();
        if (!active) {
          return;
        }

        setSettings({
          ...payload,
          attractBackgroundMedia: resolveMediaUrl(payload.attractBackgroundMedia),
          closedAttractBackgroundMedia: resolveMediaUrl(payload.closedAttractBackgroundMedia),
          clientBackgroundMedia: resolveMediaUrl(payload.clientBackgroundMedia),
        });
        persistSettings(payload);
      } catch (error) {
        console.error('Erreur chargement fond partagé:', error);
        const cachedPayload = restoreCachedSettings();
        if (!active || !cachedPayload) {
          return;
        }
        setSettings({
          ...cachedPayload,
          attractBackgroundMedia: resolveMediaUrl(cachedPayload.attractBackgroundMedia),
          closedAttractBackgroundMedia: resolveMediaUrl(cachedPayload.closedAttractBackgroundMedia),
          clientBackgroundMedia: resolveMediaUrl(cachedPayload.clientBackgroundMedia),
        });
      }
    };

    fetchSettings();
    if (shouldPoll) {
      intervalId = window.setInterval(fetchSettings, 15000);
    }

    return () => {
      active = false;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [location.pathname]);

  const backgroundMode = useMemo(() => {
    if (!settings || !KIOSK_BACKGROUND_ROUTES.has(location.pathname)) {
      return null;
    }

    const openingStatus = getOpeningStatus(settings);
    const attractBackgroundMedia = openingStatus.isOpen
      ? settings.attractBackgroundMedia
      : (settings.closedAttractBackgroundMedia || settings.attractBackgroundMedia);
    const attractBackgroundType = openingStatus.isOpen
      ? settings.attractBackgroundType
      : (settings.closedAttractBackgroundType || settings.attractBackgroundType);

    const isClientRoute = location.pathname === '/welcome' || location.pathname === '/client';
    const clientBackgroundEnabled = settings.clientBackgroundEnabled !== false;
    const clientBackgroundMedia = clientBackgroundEnabled
      ? (settings.clientBackgroundMedia || settings.attractBackgroundMedia)
      : (settings.attractBackgroundMedia || '');
    const clientBackgroundType = clientBackgroundEnabled
      ? (settings.clientBackgroundType || settings.attractBackgroundType || 'image')
      : (settings.attractBackgroundType || 'image');

    return {
      attract: {
        media: attractBackgroundMedia,
        type: attractBackgroundType || 'image',
        active: location.pathname === '/' || location.pathname === '/veille',
      },
      client: {
        media: clientBackgroundMedia,
        type: clientBackgroundType,
        active: isClientRoute,
      },
    };
  }, [location.pathname, settings]);

  if (!backgroundMode) {
    return null;
  }

  const renderLayer = ({ media, type, active }, key) => {
    const className = `kiosk-global-background-layer ${active ? 'is-active' : ''}`;

    return (
      <div key={key} className={className} aria-hidden="true">
        {media ? (
          type === 'video' ? (
            <video className="kiosk-global-background-media" src={media} autoPlay loop muted playsInline preload="auto" />
          ) : (
            <img className="kiosk-global-background-media" src={media} alt="" />
          )
        ) : (
          <div className="kiosk-global-background-fallback" />
        )}
      </div>
    );
  };

  return (
    <>
      {renderLayer(backgroundMode.attract, 'attract')}
      {renderLayer(backgroundMode.client, 'client')}
      <div className="kiosk-global-background-overlay" />
    </>
  );
}

function RequireKioskAccess() {
  const location = useLocation();
  const [accessState, setAccessState] = useState('checking');

  useEffect(() => {
    let active = true;

    const verifyAccess = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setAccessState('denied');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/auto-login`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!active) {
          return;
        }

        const payload = response.ok ? await response.json().catch(() => null) : null;
        if (payload?.token) {
          localStorage.setItem('authToken', payload.token);
        }
        if (payload?.account) {
          localStorage.setItem('kioskAccount', JSON.stringify(payload.account));
        }
        if (response.ok) {
          rememberVerifiedKioskSession(payload?.token || token, payload?.account);
        } else {
          forgetVerifiedKioskSession();
        }
        setAccessState(response.ok ? 'granted' : 'denied');
      } catch (error) {
        if (!active) {
          return;
        }
        setAccessState(hasVerifiedOfflineKioskSession() ? 'offline' : 'denied');
      }
    };

    verifyAccess();

    return () => {
      active = false;
    };
  }, []);

  if (accessState === 'checking') {
    return null;
  }

  if (accessState === 'denied' || (accessState === 'offline' && location.pathname !== '/client')) {
    return <Navigate to="/admin" replace state={{ from: location }} />;
  }

  return <><BecaisseAudienceVisit /><Outlet /></>;
}

function RequireAdminAccess() {
  const location = useLocation();
  const [accessState, setAccessState] = useState('checking');

  useEffect(() => {
    let active = true;

    const verifyAccess = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setAccessState('denied');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/auto-login`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!active) {
          return;
        }

        if (response.status === 401 || response.status === 403) {
          setAccessState('denied');
          return;
        }
        setAccessState('granted');
      } catch (error) {
        if (!active) {
          return;
        }
        // Keep session on transient network failures to avoid forced bounce to /admin/select.
        setAccessState('granted');
      }
    };

    verifyAccess();
    return () => {
      active = false;
    };
  }, []);

  if (accessState === 'checking') {
    return <div className="panel-empty">Vérification de session admin...</div>;
  }

  if (accessState === 'denied') {
    return <Navigate to="/admin" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function ProductWorkspaceBrand({ product }) {
  const identity = PRODUCT_IDENTITIES[product];
  return <div className="product-workspace-brand">
    <picture><source media="(max-width: 680px)" srcSet={`/static/branding-v5/${product}/compact.svg`} />
      <img src={`/static/branding-v4/${product}/horizontal.svg`} alt={identity.name} />
    </picture>
    <span>{identity.workspace}</span>
  </div>;
}

function canSeeProductOptions() {
  try { return JSON.parse(localStorage.getItem('kioskAccount') || '{}').role === 'admin'; }
  catch (_) { return false; }
}

function AdminInterfaceSelector() {
  const product = productForLocation(useLocation());
  const identity = PRODUCT_IDENTITIES[product];
  const [productionZones, setProductionZones] = useState([]);
  const account = (() => {
    try {
      return JSON.parse(localStorage.getItem('kioskAccount') || '{}');
    } catch (error) {
      return {};
    }
  })();

  useEffect(() => {
    let active = true;
    const loadZones = async () => {
      try {
        const response = await fetch(`${API_URL}/api/production-zones`, {
          headers: buildKioskAuthHeaders(),
        });
        if (!response.ok) {
          return;
        }
        const payload = await response.json();
        if (!active) {
          return;
        }
        setProductionZones(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (active) {
          setProductionZones([]);
        }
      }
    };
    loadZones();
    return () => {
      active = false;
    };
  }, []);

  const features = account?.features || {};
  const links = [
    ...(features.borneEnabled !== false
      ? [{ path: '/client', title: 'Borne', desc: 'Interface client tactile.' }]
      : []),
    ...(features.caisseEnabled
      ? [
          { path: '/caisse', title: 'Caisse', desc: 'Prise de commande comptoir / salle.' },
          { path: '/pad', title: 'Pad', desc: 'Caisse mobile téléphone/tablette.' },
        ]
      : []),
    { path: `/admin/dashboard?product=${product}`, title: 'Admin', desc: 'Catalogue, réglages et options de votre établissement.' },
    ...(canSeeProductOptions() ? [{ path: `/admin/options?product=${product}`, title: 'Mes options', desc: 'Accès autorisés, limites et configuration de vos services.' }] : []),
    { path: '/panel', title: 'Panel', desc: 'Suivi commandes et encaissement.' },
    { path: '/etat', title: 'État client', desc: 'Commandes en préparation et prêtes, en lecture seule.' },
    ...productionZones.map((zone) => ({
      path: `/cuisine?zone=${zone.id}`,
      title: `Cuisine ${zone.name || `Zone ${zone.id}`}`,
      desc: `Ecran cuisine dédié zone ${zone.name || zone.id}.`,
    })),
  ];

  return (
    <main className="admin-select-page">
      <section className="admin-select-shell">
        <header className="admin-select-head">
          <ProductWorkspaceBrand product={product} />
          <span className="admin-login-kicker">Connexion réussie</span>
          <h1>{identity.workspace}</h1>
          <p>
            Compte actif: <strong>{account?.name || account?.username || 'borne'}</strong>
          </p>
        </header>
        <WorkstationSettings canBind={String(account?.role || '').toLowerCase() === 'admin'} />
        <div className="admin-select-grid">
          {links.map((entry) => (
            <Link key={entry.path} to={entry.path} className="admin-select-card">
              <h2>{entry.title}</h2>
              <p>{entry.desc}</p>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}

function AppShell() {
  const location = useLocation();
  const product = productForLocation(location);
  const isProductWorkspace = /^\/admin\/(select|dashboard|options)\/?$/.test(location.pathname);
  const [productionZones, setProductionZones] = useState([]);
  const pathname = location.pathname.toLowerCase().replace(/\/+$/, '') || '/';
  const hideNav = PROTECTED_KIOSK_ROUTES.has(pathname)
    || pathname.startsWith('/admin')
    || pathname.startsWith('/master')
    || /^\/(?:c|chat|reservation|fid|loyalty)(?:\/|$)/.test(pathname);

  useEffect(() => {
    if (hideNav) return;
    let active = true;
    const loadZones = async () => {
      try {
        const response = await fetch(`${API_URL}/api/production-zones`, {
          headers: buildKioskAuthHeaders(),
        });
        if (!response.ok) {
          return;
        }
        const payload = await response.json();
        if (!active) {
          return;
        }
        setProductionZones(Array.isArray(payload) ? payload : []);
      } catch (error) {
        if (active) {
          setProductionZones([]);
        }
      }
    };
    loadZones();
    return () => {
      active = false;
    };
  }, [hideNav]);

  return (
    <div className={isProductWorkspace ? `product-theme product-theme--${product} product-workspace` : undefined}>
      <KioskBackgroundLayer />
      <RouteErrorBoundary>
        <Routes>
          <Route element={<RequireKioskAccess />}>
            <Route path="/" element={<AttractScreen />} />
            <Route path="/veille" element={<AttractScreen />} />
            <Route path="/welcome" element={<LoyaltyEntryScreen />} />
            <Route path="/client" element={<ClientView />} />
            <Route path="/panel" element={<PanelView />} />
            <Route path="/etat" element={<EtatView />} />
            <Route path="/cuisine" element={<CuisineView />} />
            <Route path="/caisse" element={<CaisseView mode="desktop" />} />
            <Route path="/caisse/reservations" element={<CaisseReservationsView />} />
            <Route path="/pad" element={<CaisseView mode="pad" />} />
          </Route>
          <Route path="/fid" element={<ExternalBefidRedirect />} />
          <Route path="/loyalty/validate" element={<LoyaltyValidationScreen />} />
          <Route path="/c/:slug" element={<ClickCollectView />} />
          <Route path="/c/:slug/success" element={<ClickCollectStatus status="success" />} />
          <Route path="/c/:slug/cancel" element={<ClickCollectStatus status="cancel" />} />
          <Route path="/c/:slug/suivi/:token" element={<ClickCollectTrackingView />} />
          <Route path="/chat/:slug" element={<CustomerChatbotPage />} />
          <Route path="/reservation" element={<ReservationPage />} />
          <Route path="/reservation/:slug" element={<ReservationPage />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route element={<RequireAdminAccess />}>
            <Route path="/admin/select" element={<AdminInterfaceSelector />} />
            <Route path="/admin/dashboard" element={<><header className="product-dashboard-heading"><ProductWorkspaceBrand product={product} />{canSeeProductOptions() && <Link to={`/admin/options?product=${product}`}>Mes options</Link>}</header><AdminDashboard /></>} />
            <Route path="/admin/options" element={<ProductOptions />} />
          </Route>
          <Route path="/master" element={<MasterConsole />} />
        </Routes>
      </RouteErrorBoundary>
      {!hideNav && (
        <nav>
          <Link to="/client">Borne</Link>
          <span className="nav-divider">|</span>
          <Link to="/panel">Panel</Link>
          <span className="nav-divider">|</span>
          <Link to="/cuisine">CUISINE</Link>
          {productionZones.map((zone) => (
            <React.Fragment key={`nav-zone-${zone.id}`}>
              <span className="nav-divider">|</span>
              <Link to={`/cuisine?zone=${zone.id}`}>CUISINE {zone.name || zone.id}</Link>
            </React.Fragment>
          ))}
          <span className="nav-divider">|</span>
          <Link to="/admin/select">Admin</Link>
        </nav>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <BrandMetadata />
      <AppShell />
    </Router>
  );
}

export default App;
